import { useState, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import { useGetDataSource } from 'automation-ui-formula-editor/data-sources/hooks/useGetDataSource';
import { isVisibleWhenSearchFiltered } from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/utils/isVisibleWhenSearchFiltered';
import { isPropertyTypeAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isPropertyTypeAllowListed';
import { isPropertyFieldTypeAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isPropertyFieldTypeAllowListed';
import { DataSourceType } from 'automation-ui-formula-editor/data-passing-panel/types/dataSourcesTypes';
import DataPanelProperty from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPanelProperty/DataPanelProperty';
import DataPassingPanelBodyNestedSkeleton from 'automation-ui-formula-editor/data-passing-panel/DataPanelRevisedUXMode/DataPassingPanelBodyNestedSkeleton';
import { isDataSourceAllowListed } from 'automation-ui-formula-editor/data-sources/utils/isDataSourceAllowListed';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY_DATA_SOURCES = {};
const DataPassingPanelBodyNestedDataSource = ({
  onDataPropertySelected,
  disabled,
  dataSourceToView,
  searchFilterString,
  dataSourceAllowList,
  propertyFieldTypeAllowList,
  propertyTypeAllowList,
  requiredPermissionLevel
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    data,
    loading,
    error
  } = useGetDataSource(dataSourceToView);
  const dataSourceDefinition = data ? data.dataSourceDefinition : {};
  const dataSourceProperties = data ? data.dataSourceProperties : EMPTY_DATA_SOURCES;
  const dataSourceItems = useMemo(() => {
    const sourceProperties = Object.values(dataSourceProperties).filter(property => {
      return isVisibleWhenSearchFiltered({
        property,
        searchFilterString
      }) && isPropertyTypeAllowListed({
        property,
        propertyTypeAllowList
      }) && isPropertyFieldTypeAllowListed({
        property,
        propertyFieldTypeAllowList,
        dataSourceType: dataSourceDefinition.dataSourceType
      });
    });
    return sourceProperties.length > 0 ? sourceProperties.sort((prevProp, currProp) => prevProp.label.toLowerCase().localeCompare(currProp.label.toLowerCase())) : [];
  }, [dataSourceProperties, propertyFieldTypeAllowList, propertyTypeAllowList, searchFilterString, dataSourceDefinition.dataSourceType]);
  const handleOnSelectProperty = dataProperty => {
    const dataCoordinate = `${dataSourceDefinition.name}.${dataProperty.name}`;
    onDataPropertySelected(dataProperty, dataSourceDefinition, dataCoordinate);
  };
  if (loading) {
    return /*#__PURE__*/_jsx(DataPassingPanelBodyNestedSkeleton, {});
  }
  if (error || !data) {
    return /*#__PURE__*/_jsx("div", {
      children: "Errored"
    });
  }
  const {
    label: dataSourceLabel,
    name: dataSourceName
  } = dataSourceDefinition;
  const isDataSourceFilteredOut = !isDataSourceAllowListed({
    prefix: dataSourceName,
    dataSourceAllowList
  });
  const renderAccordionContent = () => {
    if (!dataSourceProperties) {
      return null;
    }
    if (loading) {
      return /*#__PURE__*/_jsx(DataPassingPanelBodyNestedSkeleton, {});
    }
    if (dataSourceItems.length) {
      return dataSourceItems.sort((propertyA, propertyB) => {
        if (propertyA.label > propertyB.label) {
          return -1;
        }
        if (propertyA.label < propertyB.label) {
          return 1;
        }
        return 0;
      }).map(property => {
        const {
          name: dataPropertyName
        } = property;
        const key = `${dataSourceName}:${dataPropertyName}`;
        return /*#__PURE__*/_jsx(DataPanelProperty, {
          onDataPropertySelected: handleOnSelectProperty,
          disabled: disabled,
          fromDataSourceName: dataSourceName,
          dataProperty: property,
          isPropertyTypeCompatible: isPropertyTypeAllowListed({
            property,
            propertyTypeAllowList
          }) && isPropertyFieldTypeAllowListed({
            property,
            propertyFieldTypeAllowList,
            dataSourceType: dataSourceDefinition.dataSourceType
          }),
          requiredPermissionLevel: requiredPermissionLevel,
          isDataSourceFilteredOut: isDataSourceFilteredOut,
          hideEditPropertyLink: true
        }, key);
      });
    }

    // Empty state if there are no items
    return /*#__PURE__*/_jsx(DataPanelProperty, {
      onDataPropertySelected: handleOnSelectProperty,
      fromDataSourceName: '',
      disabled: true // keep disabled
      ,
      dataProperty: {
        name: '',
        label: searchFilterString ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-filtered-properties-found"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.no-properties"
        }),
        description: '',
        dataPropertyType: DataSourceType.EMPTY
      },
      isPropertyTypeCompatible: true,
      requiredPermissionLevel: "none"
    }, 'no_');
  };
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(UIAccordionItem, {
      "data-test-id": `data-panel-nested-property-group-${dataSourceToView.prefix}`,
      title: /*#__PURE__*/_jsx(Text, {
        typography: "heading-50",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.data-passing-panel.ux-revised-mode.nested-data-source-label",
          options: {
            dataSourceLabel
          }
        })
      }),
      size: 'small',
      open: isOpen || !!(searchFilterString && dataSourceItems.length),
      onOpenChange: () => setIsOpen(!isOpen),
      renderContent: () => renderAccordionContent()
    }, dataSourceToView.prefix)
  });
};
export default DataPassingPanelBodyNestedDataSource;